import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import CalendarView from '@/components/CalendarView';
import { Header } from '@/components/Header';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Task } from '@/types';

const TaskCard: React.FC<{ task: Task }> = ({ task }) => {
  const navigate = useNavigate();
  
  const getCategoryStyles = (category: string) => {
    switch (category.toLowerCase()) {
      case 'learning':
        return 'bg-gradient-to-br from-amber-500/90 to-orange-400/90 border-orange-300 text-white';
      case 'personal':
        return 'bg-gradient-to-br from-violet-500/90 to-purple-400/90 border-purple-300 text-white';
      case 'work':
        return 'bg-gradient-to-br from-sky-500/90 to-blue-400/90 border-blue-300 text-white';
      case 'health':
        return 'bg-gradient-to-br from-emerald-500/90 to-green-400/90 border-green-300 text-white';
      case 'shopping':
        return 'bg-gradient-to-br from-teal-500/90 to-cyan-400/90 border-cyan-300 text-white';
      case 'gym':
        return 'bg-gradient-to-br from-rose-500/90 to-pink-400/90 border-pink-300 text-white';
      default:
        return 'bg-gradient-to-br from-gray-500/90 to-slate-400/90 border-slate-300 text-white';
    }
  };

  return (
    <div
      onClick={() => navigate(`/task/${task.id}`)}
      className={cn(
        "group cursor-pointer rounded-xl p-3 border transition-all duration-300",
        "hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm",
        getCategoryStyles(task.category)
      )}
    >
      <div className="space-y-1.5">
        <div className="flex items-start justify-between">
          <h3 className="font-extrabold truncate text-base">
            {task.title}
          </h3>
          {task.isRecurring && (
            <Badge variant="outline" className="text-[10px] border-white/40 bg-white/10 text-white backdrop-blur-sm font-extrabold px-1 py-0 ml-1.5">
              ↻
            </Badge>
          )}
        </div>

        {task.description && (
          <p className="text-xs font-bold line-clamp-2 text-white/90">
            {task.description}
          </p>
        )}

        <div className="flex items-center gap-1.5">
          <Badge className="font-extrabold capitalize text-[10px] px-1.5 py-0 bg-white/20 text-white border-white/10">
            {task.category}
          </Badge>
          <Badge variant="outline" className="font-extrabold text-[10px] px-1.5 py-0 border-white/20 text-white">
            {new Date(task.dueDate!).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Badge>
        </div>
      </div>
    </div>
  );
};

const Calendar: React.FC = () => {
  const navigate = useNavigate();
  const { tasks } = useApp();

  const headerAction = (
    <Button 
      onClick={() => navigate('/create')}
      className="bg-gray-900 hover:bg-gray-800 text-white font-bold"
    >
      <Plus className="w-4 h-4 mr-2" />
      New Task
    </Button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        title="Calendar"
        subtitle="View and manage your scheduled tasks"
        action={headerAction}
      />
      
      <div className="p-4 md:p-6 max-w-7xl mx-auto mt-24">
        <Card className="bg-white/80 backdrop-blur-md border border-white/20 shadow-xl">
          <CardContent className="p-6">
            <CalendarView tasks={tasks} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Calendar; 